import { ViewController } from 'elc-service';
import { SearchLoadedUseCase } from '../use-cases/SearchLoadedUseCase';
import { SearchTypeaheadUseCase } from '../use-cases/SearchTypeaheadUseCase';
import { SearchRepository } from '../repositories/SearchRepository';
import { computed, action, observable } from 'mobx';
import {
    MIN_SEARCH_LENGTH,
    DEBOUNCE_LIMIT,
    OVERLAY_RESULTS_LIMIT,
    SEARCH_TYPE_PARAM,
    PREDICTIVE_SEARCH_PARAM,
    SEARCH_TEXT_PARAM,
    TAB_TYPE,
    PREDICTIVE_CONTENT_SEARCH_PARAM,
    PREDICTIVE_PRODUCTS_SEARCH_PARAM
} from '../constants/Search';
import { events, emit } from 'elc-service-bus';
import { Breakpoints } from 'elc-base-theme';
import { Config } from '../domain/entities/Config';

export class SearchGnavInputFieldViewController extends ViewController {
    @observable private searchTerm: string;
    @observable private searchRepository: SearchRepository;
    @observable public isSearchOverlayOpen: boolean = false;
    @observable private inputValue: string = '';
    @observable private disableInput: boolean = false;
    @observable private searchDisplayFormat: string;
    @observable private currentTab: string = TAB_TYPE.PRODUCTS;
    @observable public configSearch: Config;
    @observable private enableContentSearch: boolean = false;
    @observable private articleSearch: boolean = false;

    private minSearchLength: number = MIN_SEARCH_LENGTH;
    private debounceLimit: number = DEBOUNCE_LIMIT;
    private searchResetTimeout: number;

    constructor(searchRepository: SearchRepository, config: Config) {
        super();
        this.searchRepository = searchRepository;
        this.configSearch = config;
        this.isLoading = false;
    }

    @computed
    get data() {
        return {
            productIds: this.productIds,
            skuIds: this.skuIds,
            limitItems: this.limitItems,
            items: this.items,
            isSearchOverlayOpen: this.isSearchOverlayOpen,
            inputValue: this.inputValue,
            resultsLoaded: this.searchRepository.isTypeaheadProductsLoaded,
            disableInput: this.disableInput,
            typeaheadResults: this.typeaheadResults,
            limiteTypeaheadResults: this.limiteTypeaheadResults,
            numRecs: this.numRecs,
            adjustedTerm: this.adjustedTerm,
            configSearch: this.configSearch.config,
            dynamicTouts: this.dynamicTouts,
            dynamicArticles: this.dynamicArticles,
            contentRecs: this.contentRecs,
            currentTab: this.getCurrentTab,
            articlesRecsNum: this.articlesRecsNum,
            productRecsNum: this.productRecsNum
        };
    }

    @computed
    public get endecaRedirectUrl() {
        return this.searchRepository.searchResults.endecaRedirectUrl;
    }

    @computed
    private get productIds() {
        return this.searchRepository.searchResults.productIds || [];
    }

    @computed
    private get skuIds() {
        return this.searchRepository.searchResults.skuIds || [];
    }

    @computed
    private get items() {
        return this.searchRepository.items || [];
    }

    @computed
    private get limitItems() {
        return this.items.slice(0, OVERLAY_RESULTS_LIMIT);
    }

    @computed
    private get limitSkuIds() {
        return this.skuIds.slice(0, OVERLAY_RESULTS_LIMIT);
    }

    @computed
    private get dynamicTouts() {
        return this.searchRepository.searchContentCatalog.touts || [];
    }

    @computed
    private get dynamicArticles() {
        return this.searchRepository.searchArticleCatalog.articles || [];
    }

    @action
    public setMinSearchLength(value: number) {
        this.minSearchLength = value;
    }

    @action
    public setSearchDisplayFormat(value: string) {
        this.searchDisplayFormat = value;
    }

    @computed
    private get typeaheadResults() {
        return this.searchRepository.typeahead.dimensionsTypeahead || [];
    }

    @action
    public setContentSearch(value: boolean) {
        this.enableContentSearch = value;
    }

    @computed
    private get limiteTypeaheadResults() {
        if (window.innerWidth > Breakpoints.desktop) {
            return this.typeaheadResults.slice(0, 10);
        } else {
            return this.typeaheadResults.slice(0, 5);
        }
    }

    @action
    public setCurrentContentTab = (id: string) => {
        this.currentTab = id;
    };

    @action
    public searchResults = async () => {
        if (!!this.searchTerm) {
            if (this.enableContentSearch) {
                this.articleSearch = true;
            }

            const search = {
                searchTerm: this.searchTerm,
                searchDisplayFormat: this.searchDisplayFormat,
                enableContentSearch: this.enableContentSearch,
                articleSearch: this.articleSearch
            };

            const searchLoadedUseCase = new SearchLoadedUseCase(this.searchRepository);
            const searchTypeaheadUseCase = new SearchTypeaheadUseCase(this.searchRepository);
            try {
                this.isLoading = true;
                this.searchRepository.clearRepositoryItems();
                this.searchRepository.searchTypeaheadCatalog.dimensionsTypeahead = [];
                this.setCurrentContentTab(TAB_TYPE.PRODUCTS);
                await searchLoadedUseCase.execute(search);
                await searchTypeaheadUseCase.execute(search);

                if (this.items.length === 0 && this.dynamicArticles.length) {
                    this.setCurrentContentTab(TAB_TYPE.STORIES);
                }

                this.trackSearchResults();
            } finally {
                this.isLoading = false;
            }
        }
    };

    @action
    public toggleSearchOverlay = async () => {
        this.clearInputValue();
        this.isSearchOverlayOpen = !this.isSearchOverlayOpen;
    };

    public searchTermSelected = (searchTerm: string) => () => {
        this.trackSearchTermSelected(searchTerm);
    };

    @action
    public clearInputValue = () => {
        this.inputValue = '';
    };

    @action
    private debounceSearchResults = () => {
        clearTimeout(this.searchResetTimeout);
        this.searchResetTimeout = setTimeout(() => this.searchResults(), this.debounceLimit);
    };

    public searchQuery(value: string) {
        this.searchTerm = value;
    }

    @action
    public handleChange = (event: { value: string }): void => {
        this.searchRepository.setSearchResultsLoaded(false);
        this.inputValue = event.value;
        if (this.inputValue.length >= this.minSearchLength) {
            this.searchTerm = this.inputValue;
            this.debounceSearchResults();
        }
    };

    @computed
    private get numRecs() {
        this.searchRepository.searchResults.numRecs.totalNumRecs =
            this.searchRepository.searchResults.numRecs.totalNumRecs - this.dynamicTouts.length;

        return this.searchRepository.searchResults.numRecs;
    }

    @computed
    private get contentRecs() {
        return this.searchRepository.searchContentCatalog.numContentRecs.contentRecNumber;
    }

    @computed
    private get articlesRecsNum() {
        return this.searchRepository.searchArticleCatalog.numContentRecs.articlesRecNumber;
    }

    @computed
    private get productRecsNum() {
        return (
            this.searchRepository.searchResults.numRecs.contentTotalNumRecs -
            this.dynamicTouts.length
        );
    }

    @computed
    private get adjustedTerm() {
        return this.searchRepository.searchResults.adjustedTerm;
    }

    @action
    public onKeyUp = (
        event: { key: string; value: string },
        siteUrl: string,
        redirect: boolean,
        enableContentSearch: boolean
    ): void => {
        if (event.key === 'Enter' && event.value) {
            this.disableInput = true;
            let redirectUrl = `${siteUrl}`;

            if (!redirect) {
                if (enableContentSearch) {
                    redirectUrl = `${siteUrl}${SEARCH_TYPE_PARAM}${
                        this.getCurrentTab === TAB_TYPE.PRODUCTS
                            ? PREDICTIVE_PRODUCTS_SEARCH_PARAM
                            : PREDICTIVE_CONTENT_SEARCH_PARAM
                    }`;
                } else {
                    redirectUrl = `${siteUrl}${SEARCH_TYPE_PARAM}${PREDICTIVE_SEARCH_PARAM}${SEARCH_TEXT_PARAM}`;
                }
            }
            emit(events.CHANGE_LOCATION, { redirectUrl });
        }
    };

    @action
    public setCurrentTab = (id: string) => {
        this.currentTab = id;
    };

    @computed
    public get getCurrentTab() {
        return this.currentTab;
    }

    private trackSearchResults() {
        const eventArgs = {
            searchTerm: this.searchTerm,
            searchType: 'predictive',
            skuIds: this.limitSkuIds
        };

        emit(events.SEARCH_PRODUCTS_QUERIED, eventArgs);
    }

    private trackSearchTermSelected(searchTerm: string) {
        emit(events.SEARCH_TERM_SELECTED, { searchTerm });
    }
}
